import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const AttainmentTable = ({ data, darkMode }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    co: true,
    po: true,
    pso: true,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleAll = () => {
    setIsExpanded(!isExpanded);
    if (isExpanded) {
      setExpandedSections({
        co: false,
        po: false,
        pso: false,
      });
    } else {
      setExpandedSections({
        co: true,
        po: true,
        pso: true,
      });
    }
  };

  if (!data) {
    return (
      <div
        className={`rounded-lg p-4 mb-4 ${
          darkMode
            ? "bg-[#0D1117] border border-[#22232B]"
            : "bg-white border border-[#D1D5DB]"
        }`}
      >
        <div
          className={`rounded-t-lg p-3 -mx-4 -mt-4 mb-3 ${
            darkMode ? "bg-[#1a1f2e]" : "bg-gray-100"
          }`}
        >
          <div className="flex justify-between items-center">
            <h3
              className="text-base font-semibold"
              style={{ color: darkMode ? "#EAEAEA" : "#1F252E" }}
            >
              CO-PO Attainment Results
            </h3>
          </div>
        </div>
        <div
          className="mt-2 bg-gray-100 dark:bg-[#1E1E2E] h-20 rounded flex items-center justify-center"
          style={{ color: darkMode ? "#B0B3B8" : "#6B7280" }}
        >
          No attainment data available
        </div>
      </div>
    );
  }

  const { coAttainment, poAttainment, psoAttainment } = data;

  // Helper function to format attainment values
  const formatAttainment = (value) => {
    if (!value) return "-";
    const num = parseFloat(value);
    return num.toFixed(4);
  };

  // Helper function to determine text color based on attainment value
  const getAttainmentColor = (value) => {
    if (!value) return darkMode ? "#C9CCD1" : "#2D3A4A";
    const num = parseFloat(value);
    if (num >= 2.5) return "#16A34A"; // Green for good attainment
    if (num >= 2.0) return "#CA8A04"; // Amber for moderate attainment
    return "#DC2626"; // Red for low attainment
  };

  return (
    <div
      className={`rounded-lg p-4 mb-4 ${
        darkMode
          ? "bg-[#0D1117] border border-[#22232B]"
          : "bg-white border border-[#D1D5DB]"
      }`}
    >
      <div
        className={`rounded-t-lg p-3 -mx-4 -mt-4 mb-3 ${
          darkMode ? "bg-[#1a1f2e]" : "bg-gray-100"
        }`}
      >
        <div className="flex justify-between items-center">
          <h3
            className="text-base font-semibold"
            style={{ color: darkMode ? "#EAEAEA" : "#1F252E" }}
          >
            CO-PO-PSO Attainment Results
          </h3>
          <button
            onClick={toggleAll}
            className={`flex items-center text-xs ${
              darkMode
                ? "text-gray-300 hover:text-white"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            {isExpanded ? (
              <>
                <ChevronUp size={14} className="mr-1" />
                Collapse All
              </>
            ) : (
              <>
                <ChevronDown size={14} className="mr-1" />
                Expand All
              </>
            )}
          </button>
        </div>
      </div>

      {/* CO Attainment Table */}
      <div className="mb-4">
        <div
          className="flex items-center cursor-pointer mb-2"
          onClick={() => toggleSection("co")}
        >
          <h4
            className="text-base font-medium"
            style={{ color: darkMode ? "#D1D5DB" : "#374151" }}
          >
            Course Outcomes (CO) Attainment
          </h4>
          {expandedSections.co ? (
            <ChevronUp
              size={16}
              className={`ml-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}
            />
          ) : (
            <ChevronDown
              size={16}
              className={`ml-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}
            />
          )}
        </div>

        {expandedSections.co && (
          <div className="overflow-x-auto">
            <table
              className={`min-w-full rounded ${
                darkMode
                  ? "bg-[#161B22] text-white"
                  : "bg-gray-50 text-gray-800"
              }`}
            >
              <thead>
                <tr>
                  <th
                    className={`p-2 text-left text-sm ${
                      darkMode
                        ? "border-b border-gray-700 bg-[#0D1117]"
                        : "border-b border-gray-200 bg-gray-100"
                    }`}
                    style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}
                  >
                    CO / Attainment
                  </th>
                  {coAttainment.map((co) => (
                    <th
                      key={co.co_id}
                      className={`p-2 text-center text-sm ${
                        darkMode
                          ? "border-b border-gray-700 bg-[#0D1117]"
                          : "border-b border-gray-200 bg-gray-100"
                      }`}
                      style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}
                    >
                      CO{co.co_number}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr
                  className={
                    darkMode ? "hover:bg-[#252525]" : "hover:bg-gray-100"
                  }
                >
                  <td
                    className={`p-2 font-medium text-sm ${
                      darkMode
                        ? "border-b border-gray-700"
                        : "border-b border-gray-200"
                    }`}
                    style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}
                  >
                    Direct
                  </td>
                  {coAttainment.map((co) => (
                    <td
                      key={`direct-${co.co_id}`}
                      className={`p-2 text-center text-sm font-semibold ${
                        darkMode
                          ? "border-b border-gray-700"
                          : "border-b border-gray-200"
                      }`}
                      style={{
                        color: getAttainmentColor(co.direct_attainment),
                      }}
                    >
                      {formatAttainment(co.direct_attainment)}
                    </td>
                  ))}
                </tr>
                <tr
                  className={
                    darkMode ? "hover:bg-[#252525]" : "hover:bg-gray-100"
                  }
                >
                  <td
                    className={`p-2 font-medium text-sm ${
                      darkMode
                        ? "border-b border-gray-700"
                        : "border-b border-gray-200"
                    }`}
                    style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}
                  >
                    Indirect
                  </td>
                  {coAttainment.map((co) => (
                    <td
                      key={`indirect-${co.co_id}`}
                      className={`p-2 text-center text-sm font-semibold ${
                        darkMode
                          ? "border-b border-gray-700"
                          : "border-b border-gray-200"
                      }`}
                      style={{
                        color: getAttainmentColor(co.indirect_attainment),
                      }}
                    >
                      {formatAttainment(co.indirect_attainment)}
                    </td>
                  ))}
                </tr>
                <tr
                  className={
                    darkMode ? "hover:bg-[#252525]" : "hover:bg-gray-100"
                  }
                >
                  <td
                    className={`p-2 font-medium text-sm ${
                      darkMode
                        ? "border-b border-gray-700"
                        : "border-b border-gray-200"
                    }`}
                    style={{
                      color: darkMode ? "#C9CCD1" : "#2D3A4A",
                      backgroundColor: darkMode
                        ? "rgba(220, 38, 38, 0.1)"
                        : "rgba(220, 38, 38, 0.05)",
                    }}
                  >
                    Final
                  </td>
                  {coAttainment.map((co) => (
                    <td
                      key={`final-${co.co_id}`}
                      className={`p-2 text-center text-sm font-bold ${
                        darkMode
                          ? "border-b border-gray-700"
                          : "border-b border-gray-200"
                      }`}
                      style={{
                        color: getAttainmentColor(co.final_attainment),
                        backgroundColor: darkMode
                          ? "rgba(220, 38, 38, 0.1)"
                          : "rgba(220, 38, 38, 0.05)",
                      }}
                    >
                      {formatAttainment(co.final_attainment)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* PO Attainment Table */}
      <div className="mb-4">
        <div
          className="flex items-center cursor-pointer mb-2"
          onClick={() => toggleSection("po")}
        >
          <h4
            className="text-base font-medium"
            style={{ color: darkMode ? "#D1D5DB" : "#374151" }}
          >
            Program Outcomes (PO) Attainment
          </h4>
          {expandedSections.po ? (
            <ChevronUp
              size={16}
              className={`ml-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}
            />
          ) : (
            <ChevronDown
              size={16}
              className={`ml-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}
            />
          )}
        </div>

        {expandedSections.po && (
          <div className="overflow-x-auto">
            <table
              className={`min-w-full rounded ${
                darkMode
                  ? "bg-[#161B22] text-white"
                  : "bg-gray-50 text-gray-800"
              }`}
            >
              <thead>
                <tr>
                  <th
                    className={`p-2 text-left text-sm ${
                      darkMode
                        ? "border-b border-gray-700 bg-[#0D1117]"
                        : "border-b border-gray-200 bg-gray-100"
                    }`}
                    style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}
                  >
                    PO / Attainment
                  </th>
                  {poAttainment.map((po) => (
                    <th
                      key={po.po_id}
                      className={`p-2 text-center text-sm ${
                        darkMode
                          ? "border-b border-gray-700 bg-[#0D1117]"
                          : "border-b border-gray-200 bg-gray-100"
                      }`}
                      style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}
                    >
                      PO{po.po_number}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr
                  className={
                    darkMode ? "hover:bg-[#252525]" : "hover:bg-gray-100"
                  }
                >
                  <td
                    className={`p-2 font-medium text-sm ${
                      darkMode
                        ? "border-b border-gray-700"
                        : "border-b border-gray-200"
                    }`}
                    style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}
                  >
                    Direct
                  </td>
                  {poAttainment.map((po) => (
                    <td
                      key={`direct-${po.po_id}`}
                      className={`p-2 text-center text-sm font-semibold ${
                        darkMode
                          ? "border-b border-gray-700"
                          : "border-b border-gray-200"
                      }`}
                      style={{
                        color: getAttainmentColor(po.direct_attainment),
                      }}
                    >
                      {formatAttainment(po.direct_attainment)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* PSO Attainment Table (only if PSO data exists) */}
      {psoAttainment && psoAttainment.length > 0 && (
        <div>
          <div
            className="flex items-center cursor-pointer mb-2"
            onClick={() => toggleSection("pso")}
          >
            <h4
              className="text-base font-medium"
              style={{ color: darkMode ? "#D1D5DB" : "#374151" }}
            >
              Program Specific Outcomes (PSO) Attainment
            </h4>
            {expandedSections.pso ? (
              <ChevronUp
                size={16}
                className={`ml-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}
              />
            ) : (
              <ChevronDown
                size={16}
                className={`ml-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}
              />
            )}
          </div>

          {expandedSections.pso && (
            <div className="overflow-x-auto">
              <table
                className={`min-w-full rounded ${
                  darkMode
                    ? "bg-[#161B22] text-white"
                    : "bg-gray-50 text-gray-800"
                }`}
              >
                <thead>
                  <tr>
                    <th
                      className={`p-2 text-left text-sm ${
                        darkMode
                          ? "border-b border-gray-700 bg-[#0D1117]"
                          : "border-b border-gray-200 bg-gray-100"
                      }`}
                      style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}
                    >
                      PSO / Attainment
                    </th>
                    {psoAttainment.map((pso) => (
                      <th
                        key={pso.pso_id}
                        className={`p-2 text-center text-sm ${
                          darkMode
                            ? "border-b border-gray-700 bg-[#0D1117]"
                            : "border-b border-gray-200 bg-gray-100"
                        }`}
                        style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}
                      >
                        PSO{pso.pso_number}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr
                    className={
                      darkMode ? "hover:bg-[#252525]" : "hover:bg-gray-100"
                    }
                  >
                    <td
                      className={`p-2 font-medium text-sm ${
                        darkMode
                          ? "border-b border-gray-700"
                          : "border-b border-gray-200"
                      }`}
                      style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}
                    >
                      Direct
                    </td>
                    {psoAttainment.map((pso) => (
                      <td
                        key={`direct-${pso.pso_id}`}
                        className={`p-2 text-center text-sm font-semibold ${
                          darkMode
                            ? "border-b border-gray-700"
                            : "border-b border-gray-200"
                        }`}
                        style={{
                          color: getAttainmentColor(pso.direct_attainment),
                        }}
                      >
                        {formatAttainment(pso.direct_attainment)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div
        className="mt-4 pt-3 border-t text-xs"
        style={{ borderColor: darkMode ? "#22232B" : "#D1D5DB" }}
      >
        <h5
          className="font-medium mb-2"
          style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}
        >
          Attainment Scale:
        </h5>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center">
            <div className="w-2 h-2 mr-1 bg-green-600 rounded-sm"></div>
            <span style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}>
              Good (≥ 2.5)
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 mr-1 bg-amber-500 rounded-sm"></div>
            <span style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}>
              Moderate (2.0-2.49)
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 mr-1 bg-red-600 rounded-sm"></div>
            <span style={{ color: darkMode ? "#C9CCD1" : "#2D3A4A" }}>
              Weak (&lt; 2.0)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttainmentTable;
